import { Sidebar } from "./components/Sidebar";
import {
  Switch,
  Route,
  RouteProps,
  Redirect,
  useHistory,
  BrowserRouter,
} from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import { LoginCallback, Security } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { MsalProvider } from "@azure/msal-react";
import { Metrics } from "./pages/Metrics";
import { CountStatus } from "./pages/CountStatus";
import { Unauthorized } from "./pages/Unauthorized";
import { oktaAuthOptions } from "./utils/configuration";
import { CountStatusDetails } from "./pages/CountStatusDetails";
import {
  GroupProtectedRoute,
  GroupProtectedRouteChildren,
} from "./components/GroupProtectedRoute";
import { UNAUTHORIZED_PATH } from "./utils/authorization";
import {
  EntraAuthProvider,
  OktaAuthProvider,
  useAuthContext,
} from "./store/AuthProvider";
import { IdpDiscoveryProvider, useIdp } from "./store/IdpDiscoveryProvider";
import { msalInstance } from "./utils/msalConfig";

const oktaAuth = new OktaAuth(oktaAuthOptions);

/**
 * Renders the page only when authenticated; otherwise sends the user to `/`
 * so Sidebar can show the local Sign In screen (no automatic IdP redirect).
 */
const OktaProtectedRoute = ({
  component: Component,
  ...routeProps
}: RouteProps & { component: React.ComponentType }) => {
  const { isAuthenticated } = useAuthContext();
  return (
    <Route
      {...routeProps}
      render={(props) =>
        isAuthenticated ? <Component {...props} /> : <Redirect to='/' />
      }
    />
  );
};

const EntraProtectedRoute = ({
  children,
  ...routeProps
}: RouteProps & { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthContext();
  return (
    <Route
      {...routeProps}
      render={() => (isAuthenticated ? children : <Redirect to='/' />)}
    />
  );
};

/**
 * Common layout shared by both IdP subtrees.
 */
const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className='relative min-h-screen lg:flex'>
    <ToastContainer autoClose={4000} transition={Slide} />
    <Sidebar />
    <div className='w-full min-h-screen bg-background overflow-auto'>
      {children}
    </div>
  </div>
);

/**
 * Okta authentication subtree — LoginCallback on `/` handles the OIDC redirect;
 * protected routes defer to the local Sign In screen instead of SecureRoute.
 */
function OktaApp() {
  const history = useHistory();

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <OktaAuthProvider>
        <Shell>
          <Switch>
            <GroupProtectedRoute
              path='/metrics'
              exact
              requiredModule='metrics'
              component={Metrics}
            />
            <GroupProtectedRoute
              path='/count/:id/total/:total'
              requiredModule='count'
              component={CountStatusDetails}
            />
            <GroupProtectedRoute
              path='/count'
              requiredModule='count'
              component={CountStatus}
            />
            <OktaProtectedRoute path={UNAUTHORIZED_PATH} component={Unauthorized} />
            <Route path='/' component={LoginCallback} />
          </Switch>
        </Shell>
      </OktaAuthProvider>
    </Security>
  );
}

/**
 * Entra ID authentication subtree. The redirect callback is processed in
 * index.tsx before render; protected routes redirect to `/` when logged out
 * so Sidebar can show the local Sign In screen.
 */
function EntraApp() {
  return (
    <MsalProvider instance={msalInstance}>
      <EntraAuthProvider>
        <Shell>
          <Switch>
            <GroupProtectedRouteChildren path='/metrics' exact requiredModule='metrics'>
              <Metrics />
            </GroupProtectedRouteChildren>
            <GroupProtectedRouteChildren
              path='/count/:id/total/:total'
              requiredModule='count'
            >
              <CountStatusDetails />
            </GroupProtectedRouteChildren>
            <GroupProtectedRouteChildren path='/count' requiredModule='count'>
              <CountStatus />
            </GroupProtectedRouteChildren>
            <EntraProtectedRoute path={UNAUTHORIZED_PATH}>
              <Unauthorized />
            </EntraProtectedRoute>
            <Route path='/' render={() => null} />
          </Switch>
        </Shell>
      </EntraAuthProvider>
    </MsalProvider>
  );
}

function App() {
  const idp = useIdp();
  return idp === "entra" ? <EntraApp /> : <OktaApp />;
}

const AppWithProviders = () => {
  return (
    <BrowserRouter>
      <IdpDiscoveryProvider>
        <App />
      </IdpDiscoveryProvider>
    </BrowserRouter>
  );
};

export default AppWithProviders;
